import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import Navigation from "./components/Navigation";
import Hero from "./components/Hero";
import Profile from "./components/Profile";
import WellbeingTools from "./components/WellbeingTools";
import AIToolsPreview from "./components/AIToolsPreview";
import Blog from "./components/Blog";
import Footer from "./components/Footer";
import Newsletter from "./components/Newsletter";
import { featuredArticles } from "./content/articles";
import { AIToolPage, BrowserToolPage, ToolsHubPage } from "./pages/ToolsPages";
import ArticlePage from "./pages/ArticlePage";
import NotesPage from "./pages/NotesPage";
import { browserTools } from "./toolCatalog";

const PublisherStudio = lazy(() => import("./components/PublisherStudio"));

function usePublishedPosts() {
  const [posts, setPosts] = useState(featuredArticles);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    fetchPublishedPosts(controller.signal)
      .then((remotePosts) => setPosts(remotePosts))
      .catch((requestError) => {
        if (requestError.name === "AbortError") return;
        setError("My notes are temporarily unavailable. Please try again shortly.");
      })
      .finally(() => {
        setLoading(false);
      });
    return () => controller.abort();
  }, []);

  return { posts, loading, error };
}

async function fetchPublishedPosts(signal) {
  const response = await fetch("/api/posts", { signal, headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error("Published posts request failed");
  const payload = await response.json();
  if (!Array.isArray(payload.posts)) throw new Error("Published posts response is invalid");
  return payload.posts;
}

export default function App() {
  const [location, setLocation] = useState(() => ({ pathname: window.location.pathname, hash: window.location.hash }));
  const { posts, loading, error } = usePublishedPosts();

  useEffect(() => {
    const handleLocation = () => setLocation({ pathname: window.location.pathname, hash: window.location.hash });
    window.addEventListener("hashchange", handleLocation);
    window.addEventListener("popstate", handleLocation);
    return () => {
      window.removeEventListener("hashchange", handleLocation);
      window.removeEventListener("popstate", handleLocation);
    };
  }, []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [location.pathname]);

  const searchItems = useMemo(
    () => [
      { title: "A note from me", group: "Explore", target: "#about", detail: "My training and approach" },
      { title: "Private tools", group: "Explore", target: "/tools", detail: "Nine ways to pause, prepare, and find words" },
      { title: "Notes from my desk", group: "Explore", target: "/notes", detail: "Mental-health education" },
      { title: "Neerja Notes by email", group: "Explore", target: "#newsletter", detail: "Subscribe to occasional letters" },
      { title: "Get support now", group: "Support", target: "#support", detail: "India crisis contacts" },
      ...posts.map((post) => ({
        title: post.title,
        group: "Articles",
        target: `/notes/${post.slug || post.id}`,
        detail: post.category || "Article",
      })),
    ],
    [posts],
  );

  if (location.hash === "#studio") {
    return (
      <Suspense fallback={<main className="studio-loading">Opening the publisher…</main>}>
        <PublisherStudio onExit={() => { window.location.hash = ""; }} />
      </Suspense>
    );
  }

  if (location.pathname === "/tools") return <ToolsHubPage />;
  if (location.pathname.startsWith("/tools/")) {
    const toolId = location.pathname.split("/").filter(Boolean)[1];
    if (browserTools.some((tool) => tool.id === toolId)) return <BrowserToolPage toolId={toolId} />;
    return <AIToolPage toolId={toolId} />;
  }
  if (location.pathname === "/notes") return <NotesPage posts={posts} loading={loading} error={error} />;
  if (location.pathname.startsWith("/notes/")) {
    const slug = decodeURIComponent(location.pathname.split("/").filter(Boolean)[1] || "");
    const post = posts.find((item) => (item.slug || item.id) === slug);
    return <ArticlePage post={post} loading={loading} />;
  }

  return (
    <>
      <Navigation items={searchItems} />
      <main id="main">
        <Hero />
        <Profile />
        <WellbeingTools />
        <AIToolsPreview />
        <Blog posts={posts} loading={loading} error={error} />
        <Newsletter />
        <SupportPanel />
      </main>
      <Footer />
    </>
  );
}

function SupportPanel() {
  return (
    <section className="support-panel" id="support" aria-labelledby="support-title">
      <div className="support-panel__copy">
        <p className="support-panel__context">If this feels urgent</p>
        <h2 id="support-title">You do not have to hold it alone.</h2>
        <p>
          If you may harm yourself or someone else, call India’s emergency service now. For
          confidential, 24-hour mental-health support, Tele-MANAS connects you with trained support.
        </p>
      </div>
      <div className="support-panel__actions" aria-label="Crisis support contacts">
        <a className="button button--ink" href="tel:112">Call emergency · 112</a>
        <a className="button button--quiet" href="tel:14416">Call Tele-MANAS · 14416</a>
      </div>
      <p className="support-panel__note">
        These tools are educational and are not a diagnosis, treatment, or emergency service.
      </p>
    </section>
  );
}
